import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppError } from "../errorsHandlers/app-error.interface";
import { ApiResponse } from "../sharedModels/shared.interface";
import { SessionService } from "./session.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private publicApiUrl: string = environment.apiUrl;
  private protectedApiUrl: string = environment.mngtUrl;

  constructor(public http: HttpClient, private sessionService: SessionService) {}

  public get(endpoint: string, params?: Record<string, string | number | boolean | string[]>, publicUrl = false): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };

    if (params) {
      reqOpts.params = new HttpParams();
      for (const k in params) {
        if (params[k]) {
          reqOpts.params = reqOpts.params.set(k, params[k].toString());
        }
      }
    }

    reqOpts.headers = this.appendHeaders(reqOpts);
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    const options = {
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "json" as const,
    };
    return this.http.get(url + "/" + endpoint, options).pipe(
      switchMap((result: ApiResponse) => {
        if (!result.success) {
          return throwError({
            global: false,
            message: result.message,
          } as AppError);
        } else {
          return of(result.data);
        }
      })
    );
  }

  public getBlob(endpoint: string, params?: Record<string, string | number | boolean>, publicUrl = false): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };

    if (params) {
      reqOpts.params = new HttpParams();
      for (const k in params) {
        if (params[k]) {
          reqOpts.params = reqOpts.params.set(k, params[k].toString());
        }
      }
    }

    reqOpts.headers = this.appendHeaders(reqOpts);
    const options = {
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "blob" as "json",
    };
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    return this.http.get(url + "/" + endpoint, options).pipe(
      switchMap((result: ApiResponse | Blob) => {
        if ((result as ApiResponse).success === false) {
          return throwError({
            global: false,
            message: "error",
          } as AppError);
        } else {
          return of(result as Blob);
        }
      })
    );
  }

  public post(
    endpoint: string,
    body: unknown,
    params?: Record<string, string | number | boolean>,
    isFormData?: boolean,
    publicUrl = false
  ): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };
    reqOpts.headers = isFormData ? { "x-access-token": this.sessionService.token } : this.appendHeaders(reqOpts);
    if (params) {
      for (const k in params) {
        if (params[k]) {
          reqOpts.params = reqOpts.params.set(k, params[k].toString());
        }
      }
    }
    const options = {
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "json" as const,
    };
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    return this.http.post(url + "/" + endpoint, body, options).pipe(
      switchMap((result: ApiResponse) => {
        if (!result.success) {
          return throwError({
            global: false,
            message: result.message,
            additionalData: result.additionalData,
          } as AppError);
        } else {
          return of(result.data);
        }
      })
    );
  }

  public put(endpoint: string, body: unknown, publicUrl = false): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };
    reqOpts.headers = this.appendHeaders(reqOpts);
    const options = {
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "json" as const,
    };
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    return this.http.put(url + "/" + endpoint, body, options).pipe(
      switchMap((result: ApiResponse) => {
        if (!result.success) {
          return throwError({
            global: false,
            message: result.message,
            additionalData: result.additionalData,
          } as AppError);
        } else {
          return of(result.data);
        }
      })
    );
  }

  public deleteWithParams(endpoint: string, params?: Record<string, string | number | boolean>, publicUrl = false): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };

    if (params) {
      reqOpts.params = new HttpParams();
      for (const k in params) {
        if (params[k]) {
          reqOpts.params = reqOpts.params.set(k, params[k].toString());
        }
      }
    }
    reqOpts.headers = this.appendHeaders(reqOpts);
    const options = {
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "json" as const,
    };
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    return this.http.request("delete", url + "/" + endpoint, options).pipe(
      switchMap((result: ApiResponse) => {
        if (!result.success) {
          return throwError({
            global: false,
            message: result.message,
          } as AppError);
        } else {
          return of(result.data);
        }
      })
    );
  }

  public delete(endpoint: string, body: unknown, publicUrl = false): Observable<unknown> {
    const reqOpts = {
      params: new HttpParams(),
      headers: null,
    };
    reqOpts.headers = this.appendHeaders(reqOpts);
    const options = {
      body,
      headers: reqOpts.headers,
      params: reqOpts.params,
      responseType: "json" as const,
    };
    const url = publicUrl ? this.publicApiUrl : this.protectedApiUrl;
    return this.http.request("delete", url + "/" + endpoint, options).pipe(
      switchMap((result: ApiResponse) => {
        if (!result.success) {
          return throwError({
            global: false,
            message: result.message,
          } as AppError);
        } else {
          return of(result.data);
        }
      })
    );
  }

  private appendHeaders(reqOpts?: { headers: Record<string, unknown> }) {
    const headers = {
      ...reqOpts.headers,
      "Content-Type": "application/json",
    };

    if (this.sessionService.token) {
      headers["x-access-token"] = this.sessionService.token;
    }

    return headers;
  }

  public get serverPrefix(): string {
    return environment.production ? "001" : "002";
  }
}
